import { assertConsequence, t, type Consequence } from '@bms2/shared';
import { ModalCancel, ModalShell } from '../modals/ModalShell';
import styles from './dialog.module.css';

/**
 * The ordinary Confirmation dialog (piece 3): lighter than the typed-phrase
 * destructive one, and reached by every Button colour but Destructive. Its
 * consequence is its content, never its title; "Are you sure?" alone is
 * refused, and a consequence that counts records lists them by name. The
 * confirm action sits last and needs an explicit click.
 *
 * Since piece 5 it renders through the Modal shell rather than its own
 * `dialog`: one construction carries the top layer, the focus return and the
 * stacking rule, so this dialog inherits all three instead of restating them.
 */
export interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  consequence: Consequence;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** The control that opened it, so closing hands focus back (piece 5). */
  openerId?: string | null;
}

export const confirmationDialogDocumentedStates = ['closed', 'open'] as const;

export function ConfirmationDialog({ open, title, consequence, confirmLabel, onConfirm, onCancel, openerId = null }: ConfirmationDialogProps) {
  assertConsequence(consequence);
  return (
    <ModalShell
      open={open}
      title={title}
      onCancel={onCancel}
      openerId={openerId}
      dataModal="confirmation"
      footer={
        <>
          <ModalCancel label={t('dialog.cancel')} onCancel={onCancel} />
          <button type="button" className={styles.confirmPlain} data-confirm="plain" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <p data-consequence="true">{consequence.body}</p>
      {consequence.recordNames && consequence.recordNames.length > 0 ? (
        <ul className={styles.names}>
          {consequence.recordNames.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      ) : null}
    </ModalShell>
  );
}
